// Where a request field's value came from when it isn't the AI's reading of the
// document. One label and colour per source, shared by the field badge and the
// Compare list, so a reviewer can tell the values apart at a glance.
//
// The backend keeps such a value as the field value and the AI's reading in
// extracted_value; user_input_value / auto_value carry the original override so
// it can be offered back after a reviewer switches to the AI value.

const SOURCES = {
  user_input: {
    label: 'Typed in add-in',
    badge: 'Manual Input',
    title: 'Typed in the Outlook add-in when the request was submitted',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  domain_rule: {
    label: 'Auto-fill rule',
    badge: 'Auto-filled',
    title: 'Auto-filled from the e-mail domain the order was received at, not read from the document',
    className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  },
  ai: {
    label: 'AI extracted',
    title: 'Read from the document by AI',
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  },
}

// Details of the e-mail a request came from that a template field can take as
// its value (template_field_value_source). Mirrors EMAIL_FIELDS in
// backend/src/services/field_overrides.py, which owns the field names/types.
export const EMAIL_FIELD_OPTIONS = [
  { source: 'email_sender', label: 'Sender e-mail', example: 'jane.doe@example.com' },
  { source: 'email_subject', label: 'E-mail subject', example: 'PO 4471 – urgent' },
  { source: 'email_received_at', label: 'Received date', example: '2026-10-06' },
  { source: 'email_mailbox', label: 'Receiving mailbox', example: 'orders@example.com' },
  { source: 'email_submitted_by', label: 'Submitted by', example: 'sam@example.com' },
]

const EMAIL_SOURCE_STYLE = {
  label: 'From e-mail',
  badge: 'From e-mail',
  title: 'Taken from the e-mail this request came from, not read from the document',
  className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
}
for (const { source } of EMAIL_FIELD_OPTIONS) SOURCES[source] = EMAIL_SOURCE_STYLE

/** The picker label of an e-mail detail, e.g. 'Sender e-mail'. */
export function emailFieldLabel(valueSource) {
  return EMAIL_FIELD_OPTIONS.find((o) => o.source === valueSource)?.label || null
}

// requestfields.source_type -> source, for fields whose value is an override.
const SOURCE_BY_TYPE = {
  user_input: 'user_input',
  manual: 'user_input',
  derived: 'domain_rule',
  email: 'email_sender',
}

const OVERRIDE_TYPES = new Set(['user_input', 'derived', 'email'])

// A fixed value set on the template (a NetSuite field default). Never read from
// the document; the review screen fills it from the template.
export const TEMPLATE_DEFAULT_SOURCE = 'template_default'

/** True for a template field with a fixed value from the template. */
export function isTemplateDefault(templateField) {
  return templateField?.value_source === TEMPLATE_DEFAULT_SOURCE
}

/** True for a template field filled from the source e-mail instead of the document. */
export function isEmailSourced(templateField) {
  return Boolean(
    templateField?.value_source &&
    templateField.value_source !== 'document' &&
    templateField.value_source !== TEMPLATE_DEFAULT_SOURCE
  )
}

function sourceKey(field) {
  if (!field) return null
  if (field.source_type !== 'user_input' && field.auto_source && SOURCES[field.auto_source]) {
    return field.auto_source
  }
  return SOURCE_BY_TYPE[field.source_type] || null
}

/** Badge for the field label row, or null for an AI-extracted field. */
export function fieldSourceBadge(field) {
  const source = SOURCES[sourceKey(field)]
  if (!source) return null
  const detail = field.source_type !== 'user_input' ? field.auto_detail : null
  return {
    label: source.badge,
    title: detail ? `${source.title} (${detail})` : source.title,
    className: source.className,
  }
}

/**
 * The distinct values a reviewer can choose between for an overridden field:
 * the value typed in the add-in, the automatic value and the AI's reading, in
 * that order. Empty for fields the AI filled on its own.
 */
export function valueCandidates(field) {
  if (!OVERRIDE_TYPES.has(field?.source_type)) return []
  const seen = new Set()
  const candidates = []
  for (const [key, raw] of [
    ['user_input', field.user_input_value],
    [field.auto_source, field.auto_value],
    ['ai', field.extracted_value],
  ]) {
    const source = SOURCES[key]
    if (!source || raw === null || raw === undefined) continue
    const value = String(raw)
    if (!value.trim() || seen.has(value)) continue
    seen.add(value)
    candidates.push({
      key,
      value,
      label: source.label,
      title: source.title,
      detail: key === field.auto_source ? field.auto_detail || null : null,
      className: source.className,
    })
  }
  return candidates
}

/**
 * Which candidate the field currently holds: { key, formatted }, or null when
 * the reviewer typed something else. A typed date or number is format-
 * normalised (06/10/2026 -> 2026-10-06), so an unedited field whose value
 * matches no candidate still belongs to its own source.
 */
export function activeCandidate(candidates, field, currentValue) {
  const current = String(currentValue ?? '')
  const exact = candidates.find((c) => c.value === current)
  if (exact) return { key: exact.key, formatted: false }
  if (field?.is_manually_edited) return null
  const own = candidates.find((c) => c.key === sourceKey(field))
  return own ? { key: own.key, formatted: true } : null
}
